/**
 * Chunky square particles and camera shake.
 * Both scale themselves down when the player prefers reduced motion.
 */

const GRAVITY = 480;
const MAX_PARTICLES = 320;

export function createParticles({ reducedMotion = false } = {}) {
  const items = [];

  function spawn(x, y, vx, vy, life, size, color, gravity = GRAVITY) {
    if (items.length >= MAX_PARTICLES) return;
    items.push({ x, y, vx, vy, life, maxLife: life, size, color, gravity });
  }

  return {
    items,

    /** Generic radial burst. */
    burst(x, y, { count = 10, color = '#ffffff', speed = 150, life = 0.5, size = 3 } = {}) {
      const total = reducedMotion ? Math.ceil(count / 3) : count;
      for (let i = 0; i < total; i += 1) {
        const angle = (Math.PI * 2 * i) / total + Math.random() * 0.5;
        const velocity = speed * (0.45 + Math.random() * 0.8);
        spawn(
          x,
          y,
          Math.cos(angle) * velocity,
          Math.sin(angle) * velocity,
          life * (0.6 + Math.random() * 0.6),
          size,
          color,
        );
      }
    },

    /** Brick shattering into its own colour. */
    debris(brick, colors) {
      const count = reducedMotion ? 4 : 10;
      for (let i = 0; i < count; i += 1) {
        const px = brick.x + Math.random() * brick.w;
        const py = brick.y + Math.random() * brick.h;
        spawn(
          px,
          py,
          (px - (brick.x + brick.w / 2)) * 6 + (Math.random() - 0.5) * 90,
          -60 - Math.random() * 150,
          0.42 + Math.random() * 0.35,
          Math.random() < 0.35 ? 4 : 3,
          Math.random() < 0.4 ? colors.light : colors.base,
        );
      }
    },

    /** Sparks that drift without falling, used for laser strikes. */
    spark(x, y, color = '#f0803c') {
      const count = reducedMotion ? 2 : 6;
      for (let i = 0; i < count; i += 1) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
        const velocity = 90 + Math.random() * 140;
        spawn(
          x,
          y,
          Math.cos(angle) * velocity,
          Math.sin(angle) * velocity,
          0.22 + Math.random() * 0.2,
          2,
          color,
          80,
        );
      }
    },

    update(dt) {
      for (let i = items.length - 1; i >= 0; i -= 1) {
        const p = items[i];
        p.life -= dt;
        if (p.life <= 0) {
          items.splice(i, 1);
          continue;
        }
        p.vy += p.gravity * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
      }
    },

    clear() {
      items.length = 0;
    },
  };
}

export function createShake({ reducedMotion = false } = {}) {
  let amount = 0;

  return {
    add(strength) {
      if (reducedMotion) return;
      amount = Math.min(14, amount + strength);
    },
    update(dt) {
      amount = Math.max(0, amount - dt * 26);
    },
    offset() {
      if (amount <= 0) return { x: 0, y: 0 };
      return {
        x: (Math.random() - 0.5) * amount,
        y: (Math.random() - 0.5) * amount,
      };
    },
    clear() {
      amount = 0;
    },
  };
}
